import {
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  SelectProps,
  Typography,
} from '@mui/material';
import React from 'react';

export interface DropDownItem {
  key: string | number;
  description: string;
}

export interface DropDownItemProps extends Omit<SelectProps, 'children'> {
  children: DropDownItem[];
  label?: string;
  noSelection: string;
  helperText?: string;
}

/**
 * Controlled dropdown input backed by MUI Select.
 * Wrapped with React.forwardRef so react-hook-form can attach its ref
 * to the underlying Select element without triggering a ref-forwarding warning.
 *
 * @param label - Optional label rendered above the select
 * @param children - Array of selectable items with key and description
 * @param noSelection - Placeholder text shown when no value is selected
 * @param ref - Forwarded ref attached to the inner MUI Select
 */
const DropDownInput = React.forwardRef<unknown, DropDownItemProps>(
  ({label, children, noSelection, helperText, ...other}, ref) => {
    return (
      <FormControl fullWidth>
        <Typography variant="subtitle2">{label}</Typography>
        <Select
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ref={ref as any}
          data-testid="absence-type"
          labelId="absence_type_label"
          placeholder={noSelection}
          // Prevent MUI Modal from restoring focus to the hidden native input
          // on close, which triggers an aria-hidden + focus warning in browsers
          MenuProps={{disableRestoreFocus: true}}
          {...other}
        >
          {children.map(item => (
            <MenuItem key={item.key} value={item.key}>
              {item.description}
            </MenuItem>
          ))}
        </Select>
        {helperText && <FormHelperText error>{helperText}</FormHelperText>}
      </FormControl>
    );
  }
);

DropDownInput.displayName = 'DropDownInput';

export default DropDownInput;
